import * as React from "react"
import { Box, Card, Typography, Skeleton } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"

import { statsData, isLoadingStats, newProductsDateFrom, newProductsDateTo, recentDataEnabled, recentDataDays, recentDataDateFrom, recentDataDateTo } from "./@signals"


function StatCard({ icon, label, value, subValue, color = 'primary.main', bgColor = '#f0f9ff' }) {
    return (
        <Card 
            variant="outlined"
            sx={{ 
                p: 2, 
                flex: 1,
                minWidth: 180,
                backgroundColor: bgColor,
                borderColor: 'transparent'
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'white',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                    <IconSeeker icon={icon} sx={{ color, fontSize: 20 }} />
                </Box>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color, lineHeight: 1.2 }}>
                        {value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {label}
                    </Typography>
                    {subValue && (
                        <Typography variant="caption" color="text.disabled" display="block" sx={{ fontSize: '0.65rem' }}>
                            {subValue}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Card>
    )
}

function StatCardSkeleton() {
    return (
        <Card variant="outlined" sx={{ p: 2, flex: 1, minWidth: 180 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Skeleton variant="rounded" width={40} height={40} />
                <Box>
                    <Skeleton variant="text" width={60} height={28} />
                    <Skeleton variant="text" width={80} height={16} />
                </Box>
            </Box>
        </Card>
    )
}


// Helper to format recent data label
function getRecentDataLabel() {
    const enabled = recentDataEnabled.value
    const days = recentDataDays.value
    const from = recentDataDateFrom.value
    const to = recentDataDateTo.value

    if (!enabled) return 'All data (no date filter)'

    if (from || to) {
        const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }) : null
        if (from && to) return `Data: ${formatDate(from)} - ${formatDate(to)}`
        if (from) return `Data: from ${formatDate(from)}`
        if (to) return `Data: until ${formatDate(to)}`
    }

    return `Last ${days} days`
}

export default function StatsCards() {
    const stats = statsData.value
    const isLoading = isLoadingStats.value
    const dateFrom = newProductsDateFrom.value
    const dateTo = newProductsDateTo.value
    const hasCustomRange = dateFrom && dateTo && stats?.new_custom_range !== undefined

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
            </Box>
        )
    }

    if (!stats) return null

    const percentage = parseFloat(stats.mapping_percentage) || 0
    const mappingColor = percentage >= 80 ? '#059669' : percentage >= 50 ? '#d97706' : '#dc2626'
    const mappingBg = percentage >= 80 ? '#ecfdf5' : percentage >= 50 ? '#fffbeb' : '#fef2f2'

    // Format custom date range for display
    const formatDateRange = () => {
        if (!dateFrom || !dateTo) return ''
        const from = dateFrom.split('-').slice(1).join('/')
        const to = dateTo.split('-').slice(1).join('/')
        return `${from} - ${to}`
    }

    const recentDataLabel = getRecentDataLabel()

    return (
        <Box sx={{ mb: 2 }}>
            {/* Recent Data Indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <IconSeeker icon="clock-rotate-left" sx={{ fontSize: 12, color: '#10b981' }} />
                <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 500 }}>
                    {recentDataLabel}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <StatCard
                icon="chart-pie"
                label="Mapping Rate"
                value={`${percentage.toFixed(1)}%`}
                subValue={`${stats.mapped || 0} of ${stats.total || 0}`}
                color={mappingColor}
                bgColor={mappingBg}
            />
            <StatCard
                icon="circle-exclamation"
                label="Pending"
                value={stats.unmapped || 0}
                subValue="Products to map"
                color="#dc2626"
                bgColor="#fef2f2"
            />
            {hasCustomRange ? (
                <StatCard
                    icon="calendar-range"
                    label="New Products (Custom)"
                    value={stats.new_custom_range || 0}
                    subValue={formatDateRange()}
                    color="#8b5cf6"
                    bgColor="#f5f3ff"
                />
            ) : (
                <>
                    <StatCard
                        icon="calendar-week"
                        label="New Products (7d)"
                        value={stats.new_last_7d || 0}
                        subValue="Recent products"
                        color="#6366f1"
                        bgColor="#eef2ff"
                    />
                    <StatCard
                        icon="clock"
                        label="New Products (24h)"
                        value={stats.new_last_24h || 0}
                        subValue="Today's products"
                        color="#0891b2"
                        bgColor="#ecfeff"
                    />
                </>
            )}
            </Box>
        </Box>
    )
}
